import { JSX } from "react"
import { UpgradeShared, UpgradeType } from "../../../../api/repositories/cdn/models/shared"
import { CdnUrl } from "../../../../common/constants"
import { localize } from "../../../../common/services/localization"
import { combineAddress, getDotaTextWithReplaces } from "../../../../common/utils"
import { HeroTemplateHeaderClass } from "../../layout/blocks"
import { AghanimImage, InnateImage, UpgradeImage } from "../../ui/file-contents"
import { CooldownIcon, ManaIcon } from "../../ui/icons"

export const UpgradeChangesBlock: React.FC<{
    hero: Hero
    upgrades: UpgradeShared[]
}> = ({ upgrades, hero }) => {
    const scepterUpgrades = upgrades.filter(f => f.upgradeType == 'scepter')
    const shardUpgrades = upgrades.filter(f => f.upgradeType == 'shard')
    const innateUpgrades = upgrades.filter(f => f.upgradeType == 'innate')
    
    const upgradesBlocks: JSX.Element[] = [];

    scepterUpgrades.length > 0 && upgradesBlocks.push(<UpgradeChanges type="scepter" upgrades={scepterUpgrades} className="pb-[18px] 2xl:pe-[18px] 2xl:pb-[0px]" />)
    shardUpgrades.length > 0 && upgradesBlocks.push(<UpgradeChanges type="shard" upgrades={shardUpgrades} className="pt-[18px] 2xl:ps-[18px] 2xl:pt-[0px]" />)
    innateUpgrades.length > 0 && upgradesBlocks.push(<UpgradeChanges type="innate" upgrades={innateUpgrades} className="pt-[18px] 2xl:ps-[18px] 2xl:pt-[0px]" />)

    const resultBlocks: JSX.Element[] = [];
    upgradesBlocks.forEach((block, i) => {
        resultBlocks.push(block);
        if (i < upgradesBlocks.length - 1) {
            resultBlocks.push(
                <div
                    key={`divider-${i}`}
                    className="h-[2px] lg:w-[2px] w-[240px] lg:h-[400px] bg-[#ffffff0d] m-auto"
                />
            );
        }
    });

    return <div >
        <div className={`mb-[18px] ${HeroTemplateHeaderClass} `}>
            {localize('hero_upgrades')}
        </div>
        <div className="w-[300px] lg:w-full flex flex-col 2xl:flex-row justify-between">
            {resultBlocks}
        </div>
    </div>
}

const UpgradeChanges: React.FC<{
    upgrades: UpgradeShared[]
    type: UpgradeType
    className?: string
}> = ({ upgrades, type, className }) => {
    let title = '';

    switch (type) {
        case 'scepter':
            title = 'Aghanim’s scepter'
            break;
        case 'shard':
            title = 'Aghanim’s shard'
            break;
        case 'innate':
            title = 'Innate ability'
            break;
    }


    return <div className={`${className ?? ''} lg:max-w-[50%]  xl:max-w-[30%] flex flex-col gap-[4px]`}>
        <div className="flex flex-row items-center gap-[12px] min-h-[40px]">
            {
                type == 'innate'
                    ? <InnateImage className="h-[32px]" />
                    : <AghanimImage className="" type={type} />

            }
            <div className="uppercase text-18 text-orange-dark font-semibold">
                {title}
            </div>
        </div>
        <div className="grid grid-cols-1 gap-[14px] font-['Roboto'] text-18 ">
            {
                upgrades.map(upgrade => <UpgradeChange
                    key={upgrade.description}
                    upgrade={upgrade}
                />)
            }
        </div>
    </div>
}

const UpgradeChange: React.FC<{
    upgrade: UpgradeShared
}> = ({ upgrade }) => {

    const isGrantedValue = `hero_upgrades_granted_${upgrade.isGranted}`

    return <div
        className="p-[22px] flex flex-col gap-[14px] font-['Roboto']"
        style={{
            backgroundImage: "url('/images/upgrade-bg.png')",
            backgroundRepeat: "no-repeat",
            backgroundSize: "100% 100%",
            backgroundPosition: "center"
        }}>
        {
            upgrade.upgradeType == 'innate'
                ? null
                : <div className="flex flex-row gap-[8px] items-center">
                    {
                        !upgrade.isGranted && <img className="w-[32px] h-[32px]" src={combineAddress(CdnUrl, `/shared/${upgrade.texture.toLocaleLowerCase()}`)} alt="" />
                    }
                    <div className="px-[12px] py-[4px] text-center text-[12px] text-[#9CAED6] bg-[#1F355C]">
                        {localize(isGrantedValue)}
                    </div>
                </div>
        }
        <div className="flex flex-col gap-[8px]">
            <div className="text-16" dangerouslySetInnerHTML={{ __html: getDotaTextWithReplaces(upgrade.description) }} />
            {
                (upgrade.isGranted || upgrade.extraValues.length > 0) && <div className="flex flex-col gap-[8px]">
                    {upgrade.extraValues.map(extraValue => <div
                        key={extraValue[0]}
                        className="text-[13px] flex flex-row items-center gap-[8px]"
                    >
                        <div className="text-gray" dangerouslySetInnerHTML={{ __html: extraValue[0] }}></div>
                        <div dangerouslySetInnerHTML={{ __html: extraValue[1] }}></div>
                    </div>)}
                    <div className="flex flex-row items-center gap-[12px] text-[14px] mt-[6px]">
                        {
                            upgrade.cooldown && <div className="flex flex-row items-center gap-[4px]">
                                <CooldownIcon />
                                <div>{upgrade.cooldown}</div>
                            </div>
                        }
                        {
                            upgrade.manaCost && <div className="flex flex-row items-center gap-[4px]">
                                <ManaIcon />
                                <div>{upgrade.manaCost}</div>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    </div>
}
